import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  FileText,
  BookOpen,
  Lock,
  Settings,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { ThemeToggle } from "../shared/ThemeToggle";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "sidebar-dashboard" },
  { to: "/deals", icon: Briefcase, label: "Deal Flow", testid: "sidebar-deals" },
  { to: "/intelligence", icon: FileText, label: "Intelligence", testid: "sidebar-intelligence" },
  { to: "/playbooks", icon: BookOpen, label: "Playbooks", testid: "sidebar-playbooks" },
  { to: "/deal-room", icon: Lock, label: "Deal Room", testid: "sidebar-deal-room", premium: true },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-60 border-r border-border flex flex-col sticky top-0 h-screen" data-testid="dashboard-sidebar">
        <div className="p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 flex items-center justify-center border border-[hsl(var(--gold))] rounded-sm">
              <span className="font-display font-bold text-[10px] gold-text">FT</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-medium text-sm tracking-tight">FRONTERA</div>
              <div className="text-[9px] text-muted-foreground tracking-[0.2em] uppercase">Intelligence</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5">
          <div className="overline px-3 py-2">Navigate</div>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all relative ${
                  isActive
                    ? "bg-[hsl(var(--surface-elevated))] text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-[hsl(var(--gold))] rounded-full" />
                  )}
                  <item.icon className="h-4 w-4" />
                  <span>{item.label}</span>
                  {item.premium && <Lock className="h-3 w-3 ml-auto text-muted-foreground" />}
                </>
              )}
            </NavLink>
          ))}

          {user?.is_admin && (
            <>
              <div className="overline px-3 py-2 mt-4">Admin</div>
              <NavLink
                to="/admin"
                data-testid="sidebar-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                    isActive
                      ? "bg-[hsl(var(--surface-elevated))] text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))]"
                  }`
                }
              >
                <ShieldCheck className="h-4 w-4" />
                <span>Admin Panel</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-border space-y-0.5">
          <NavLink
            to="/account"
            data-testid="sidebar-account"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive ? "bg-[hsl(var(--surface-elevated))] text-foreground" : "text-muted-foreground hover:text-foreground"
              }`
            }
          >
            <Settings className="h-4 w-4" />
            <span>Account</span>
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground w-full text-left"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>

          <div className="flex items-center justify-between px-3 py-2 mt-2 border-t border-border pt-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {user?.tier || "standard"}
            </div>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">{children}</main>
    </div>
  );
};
